import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useDialog } from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import { useSelector } from "react-redux";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import pxToRem from "theme/base/pxToRem";
import {
  DialogTitleCustom,
  Title,
} from "modules/PersonnelInformation/Pages/Main/component/modal/style";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";
import { selectAttendanceDetail } from "modules/DeclareLeave/redux/selector";
import { ApproveStatus, optionTypeLeave, statusColor } from "../const";
import { hexToRgba } from "theme/types/color";
import { ApproveStatusIcon } from "./DayLeaveType";

const AttendanceDetailsModal = () => {
  const viewDayDetailDialog = useDialog(DIALOG.VIEW_DAY_DETAIL);
  const open = useSelector(isOpenDialogSelector(DIALOG.VIEW_DAY_DETAIL));

  const { data: detail, date } = useSelector(selectAttendanceDetail);
  const timeChecking = useMemo(() => {
    const timeChecking = (detail ?? []).find(
      (i) => i.type === "timeChecking"
    )?.payload;
    const lackOfTime = (detail ?? []).find(
      (i) => i.type === "lackOfTimeChecking"
    )?.payload;
    return [
      { label: "Giờ vào", value: timeChecking?.checkInTime ?? "---" },
      { label: "Giờ ra", value: timeChecking?.checkOutTime ?? "---" },
      { label: "Đi muộn", value: `${lackOfTime?.lateDuration || "---"} phút` },
      {
        label: "Về sớm",
        value: `${lackOfTime?.earlyLeaveDuration || "---"} phút`,
      },
    ];
  }, [detail]);

  const lateEarly = useMemo(() => {
    const lateEarly = (detail ?? []).find(
      (i) => i.type === "lackOfTimeChecking"
    )?.payload;
    return [
      [
        {
          label: "Đi muộn",
          value: `${lateEarly?.lateRequest || "---"} phút`,
          status: lateEarly?.regLateApproveStatus,
          borderBottom: true,
        },
        lateEarly?.lateSecondRequest && {
          label: "Đi muộn",
          value: `${lateEarly?.lateSecondRequest || "---"} phút`,
          status: lateEarly?.regArriveLateApproveStatusSecond || "---",
          borderBottom: true,
        },
        {
          label: "Về sớm",
          value: `${lateEarly?.earlyLeaveRequest || "---"} phút`,
          status: lateEarly?.regEarlyApproveStatus,
          borderBottom: true,
        },
        lateEarly?.earlyLeaveSecondRequest && {
          label: "Về sớm",
          value: `${lateEarly?.earlyLeaveSecondRequest || "---"} phút`,
          status: lateEarly?.regArriveEarlyApproveStatusSecond || "---",
        },
      ],
    ];
  }, [detail]);

  const leave = useMemo(() => {
    const leaveDetail = (detail ?? []).find(
      (i) => i.type === "dayLeave"
    )?.payload;
    return leaveDetail
      ? leaveDetail.map((item, index) => {
          return [
            {
              label: "Loại nghỉ",
              value: optionTypeLeave[item?.leaveType]?.label,
              borderBottom: true,
            },
            {
              label: "Loại hình nghỉ",
              value: item?.leaveCategory,
            },
            {
              label: "",
              value: item?.leaveCategoryDescription,
              bgColor: item?.hexColor,
            },
          ];
        })
      : [];
  }, [detail]);

  const renderInfo = (info) =>
    info.map((item, index) => (
      <Box
        key={index}
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          padding: item?.bgColor ? pxToRem(8) : 0,

          paddingBottom: index === info.length - 1 && !item?.bgColor ? 0 : 1,
          borderBottom: item?.borderBottom
            ? `1px solid ${theme.palette.stroke.strokeDefault2}`
            : "none",
          marginBottom: item?.borderBottom ? 1.5 : 0,
          borderRadius: item?.bgColor ? pxToRem(4) : "none",
          background: item?.bgColor
            ? hexToRgba(item?.bgColor, 0.1)
            : "transparent",
        })}
      >
        <Typography sx={{ minWidth: pxToRem(60) }}>{item?.label}</Typography>
        <Typography fontSize={item?.bgColor ? 13 : 14}>
          {item?.value}
        </Typography>
        {item?.status !== undefined && (
          <Typography
            fontSize={item?.bgColor ? 13 : 14}
            minWidth={pxToRem(100)}
            textAlign={"right"}
            sx={(theme) => ({
              color: statusColor(theme, item?.status, "color"),
            })}
          >
            {ApproveStatus[item?.status] ?? "---"}
          </Typography>
        )}
      </Box>
    ));

  const sections = {
    lateEarly: {
      title: "Thông tin xin đi muộn/về sớm",
      data: lateEarly,
      status: (detail ?? []).find((i) => i.type === "lackOfTimeChecking")
        ?.payload?.status,
    },
    leave: {
      title: "Thông tin xin nghỉ",
      data: leave,
      status: ((detail ?? []).find((i) => i.type === "dayLeave")?.payload ??
        [])[0]?.status,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={() => viewDayDetailDialog.close()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitleCustom>
        <Title>{`Ngày ${date}`}</Title>
        <IconButton onClick={viewDayDetailDialog.close}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>

      <DialogContent
        sx={(theme) => ({
          background: theme.palette.background.bgTertiary,
          p: 0,
        })}
      >
        <Box
          sx={(theme) => ({
            background: theme.palette.background.bgPrimary,
            padding: 2.5,
            margin: 2,
            borderRadius: 1,
          })}
        >
          <Box>
            <Typography gutterBottom>Thông tin chấm công</Typography>
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
                border: `1px solid ${theme.palette.stroke.strokeDefault}`,
                borderRadius: 1,
                padding: `${pxToRem(12)} ${pxToRem(16)}`,
                marginBottom: 2,
                "& > :nth-child(even)": {
                  justifyContent: "end",
                },
              })}
            >
              {timeChecking.map((item) => (
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    gap: 0.5,
                  })}
                >
                  <Typography>{item?.label}: </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.text.textLink,
                    })}
                  >
                    {item?.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {Object.keys(sections).map((key, index) => {
            const section = sections[key];

            return (
              <>
                {section.data.length > 0 && (
                  <Box
                    key={key}
                    mb={index < Object.keys(sections).length - 1 ? 2 : 0}
                  >
                    <Typography gutterBottom>{section.title}</Typography>
                    {section.status && key !== "lateEarly" && (
                      <Box
                        sx={(theme) => ({
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          padding: `${pxToRem(10)} ${pxToRem(12)}`,
                          marginBottom: pxToRem(12),
                          background: statusColor(
                            theme,
                            section.status,
                            "background"
                          ),
                          borderRadius: pxToRem(4),
                        })}
                      >
                        {ApproveStatusIcon[section.status]}
                        <Typography
                          sx={(theme) => ({
                            color: statusColor(theme, section.status, "color"),
                            fontWeight: 600,
                            fontSize: pxToRem(13),
                          })}
                        >
                          {ApproveStatus[section.status]}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      display={"flex"}
                      gap={1.5}
                      width={"100%"}
                      height={"100%"}
                    >
                      {section.data.map((item) => (
                        <Box
                          sx={(theme) => ({
                            flex: 1,
                            border: `1px solid ${theme.palette.stroke.strokeDefault}`,
                            borderRadius: 1,
                            padding: `${pxToRem(12)} ${pxToRem(16)}`,
                            height: "initial",
                          })}
                        >
                          {renderInfo(item)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            );
          })}
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box sx={{ padding: 2, textAlign: "center" }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => viewDayDetailDialog.close()}
          sx={{ minWidth: 120 }}
        >
          Đóng
        </Button>
      </Box>
    </Dialog>
  );
};

export default AttendanceDetailsModal;
